/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { assign, createMachine } from 'xstate';
import { EVALUATIONS_STRINGS as STRINGS } from '../evaluations.strings';
import { getRepositoryReevaluationStatus, startRepositoryReevaluation } from '../repositoryReevaluation.api';
import { pollDelayMs } from '../repositoryReevaluation.constants';
import type { ReevaluationOutcome, ReevaluationStatus, StartOutcome } from '../repositoryReevaluation.types';

export interface RepositoryReevaluationContext {
  repositoryId: string;
  /** The run being followed: the one this machine started, or the one IQ said was already running. */
  requestId: string | null;
  /** Status polls made so far, which sets how long the next one waits. */
  pollAttempt: number;
  outcome: ReevaluationOutcome | null;
  error: string | null;
}

export type RepositoryReevaluationEvent = { type: 'OPEN_CONFIRM' } | { type: 'CLOSE_CONFIRM' } | { type: 'CONFIRM' };

type StartDone = { type: 'done.invoke.startReevaluation'; data: StartOutcome };
type StartFailed = { type: 'error.platform.startReevaluation'; data: unknown };
type PollDone = { type: 'done.invoke.getStatus'; data: ReevaluationStatus };
type PollFailed = { type: 'error.platform.getStatus'; data: unknown };

const OUTCOME_OF_STATUS: Partial<Record<ReevaluationStatus, ReevaluationOutcome>> = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  SKIPPED: 'skipped',
};

const messageOf = (data: unknown, fallback: string) =>
  data instanceof Error && data.message ? data.message : fallback;

const resetRun = assign<RepositoryReevaluationContext>({
  requestId: () => null,
  pollAttempt: () => 0,
  outcome: () => null,
  error: () => null,
});
const setRequestId = assign<RepositoryReevaluationContext, StartDone>({
  requestId: (_context, event) => event.data.requestId,
});
const setOutcomeAlreadyRunning = assign<RepositoryReevaluationContext, StartDone>({ outcome: () => 'alreadyRunning' });
const setStartError = assign<RepositoryReevaluationContext, StartFailed>({
  error: (_context, event) => messageOf(event.data, STRINGS.reevaluate.errors.start),
  outcome: () => 'error',
});
const nextAttempt = assign<RepositoryReevaluationContext, PollDone>({
  pollAttempt: (context) => context.pollAttempt + 1,
});
// Any status but RUNNING ends the watch; UNKNOWN (IQ lost the request) and anything unexpected read as finished.
const setOutcomeFromStatus = assign<RepositoryReevaluationContext, PollDone>({
  outcome: (_context, event) => OUTCOME_OF_STATUS[event.data] ?? 'finished',
});
const setPollError = assign<RepositoryReevaluationContext, PollFailed>({
  error: (_context, event) => messageOf(event.data, STRINGS.reevaluate.errors.poll),
  outcome: () => 'error',
});

/**
 * One repository's re-evaluation: confirm, start, follow the run until it ends. A failure of the start or of one status
 * poll ends it with no retry; the user can start again from every end state.
 */
export const repositoryReevaluationMachine = createMachine<RepositoryReevaluationContext, RepositoryReevaluationEvent>(
  {
    id: 'repositoryReevaluation',
    predictableActionArguments: true,
    initial: 'idle',
    context: { repositoryId: '', requestId: null, pollAttempt: 0, outcome: null, error: null },
    states: {
      idle: { on: { OPEN_CONFIRM: 'confirming' } },
      confirming: {
        on: {
          CLOSE_CONFIRM: 'idle',
          CONFIRM: { target: 'starting', actions: 'resetRun' },
        },
      },
      // CLOSE_CONFIRM is not handled here: the dialog reports it right after CONFIRM.
      starting: {
        invoke: {
          id: 'startReevaluation',
          src: 'startReevaluation',
          onDone: [
            { cond: 'startedNew', target: 'running', actions: ['setRequestId', 'notifyStarted', 'trackStarted'] },
            {
              cond: 'runningWithId',
              target: 'running',
              actions: ['setRequestId', 'notifyAlreadyRunning', 'trackAlreadyRunning'],
            },
            // Running, but IQ did not say which: nothing to poll, the refreshed list shows the row as running.
            { target: 'done', actions: ['setOutcomeAlreadyRunning', 'notifyAlreadyRunning', 'trackAlreadyRunning'] },
          ],
          onError: { target: 'startFailed', actions: ['setStartError', 'notifyStartFailed', 'trackFailed'] },
        },
      },
      running: {
        initial: 'waiting',
        states: {
          waiting: { after: { POLL_DELAY: 'polling' } },
          polling: {
            invoke: {
              id: 'getStatus',
              src: 'getStatus',
              onDone: [
                { cond: 'stillRunning', target: 'waiting', actions: 'nextAttempt' },
                { target: '#repositoryReevaluation.done', actions: 'setOutcomeFromStatus' },
              ],
              onError: { target: '#repositoryReevaluation.done', actions: 'setPollError' },
            },
          },
        },
      },
      // Nothing changed in IQ when the start failed, so unlike `done` this does not refresh the list.
      startFailed: { on: { OPEN_CONFIRM: 'confirming' } },
      done: {
        entry: ['notifySettled', 'refreshList'],
        on: { OPEN_CONFIRM: 'confirming' },
      },
    },
  },
  {
    services: {
      startReevaluation: (context) => startRepositoryReevaluation(context.repositoryId),
      getStatus: (context) => getRepositoryReevaluationStatus(context.repositoryId, context.requestId ?? ''),
    },
    guards: {
      startedNew: (_context, event) => (event as unknown as StartDone).data.kind === 'started',
      runningWithId: (_context, event) => (event as unknown as StartDone).data.requestId !== null,
      stillRunning: (_context, event) => (event as unknown as PollDone).data === 'RUNNING',
    },
    delays: {
      POLL_DELAY: (context) => pollDelayMs(context.pollAttempt),
    },
    actions: {
      resetRun,
      setRequestId,
      setOutcomeAlreadyRunning,
      setStartError,
      nextAttempt,
      setOutcomeFromStatus,
      setPollError,
      // The hook provides these: they show the toasts, refresh the list and send the analytics event.
      notifyStarted: () => undefined,
      notifyAlreadyRunning: () => undefined,
      notifyStartFailed: () => undefined,
      notifySettled: () => undefined,
      refreshList: () => undefined,
      trackStarted: () => undefined,
      trackAlreadyRunning: () => undefined,
      trackFailed: () => undefined,
    },
  }
);
